import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Div100vh from "react-div-100vh";
import HeaderB2B from "@/b2b/components/Header";
import CustomDrawer from "@/components/common/drawer";
import PrimaryButton from "@/b2b/components/PrimaryButton.jsx";
import Header from "@/components/common/Header/header";
import { fetchData, updateFunction } from "@/lib/helper";
import { startDigioWithRetry } from "@/lib/esignStartDigio";
import { format } from "date-fns";
import { ChevronLeftIcon } from "lucide-react";
import { trackConsent } from "@/lib/apis.js";
import { CONSENT_MESSAGES } from "@/lib/data.js";
import Tooltip from "@/components/common/ToolTip";
import config from "@/config";
import { useJourneyLender } from "@/lib/journeyLender";
import GromorEsignFlow from "./GromorEsignFlow";
import { fetchGromorEsignStatus, startGromorEsign } from "@/lib/gromorEsign";

// Honest, staged status for the esign/start-digio wait — no fabricated percentage,
// since the backend has no granular progress signal to back one with (see
// docs/esign-perf/PLAN.md §11). Just tells the tenant roughly what's still happening,
// on a fixed schedule, instead of a bare unlabeled "Loading...". The 30s stage is
// intentionally set past the ~20s typical wait (measured live against the perf fix,
// see docs/esign-perf/JOURNAL.md) — it should only appear on a genuinely slow attempt,
// not on a normal one, or it stops being honest.
const ESIGN_LOADING_STAGES = [
  { atMs: 0, text: "Preparing your agreement..." },
  { atMs: 8000, text: "Almost there..." },
  { atMs: 30000, text: "This can take a little longer. Please wait." },
];

export default function ESign({ theme }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState(ESIGN_LOADING_STAGES[0].text);
  const [kfsData, setKfsData] = useState(null);
  // Name/logo of THIS journey's lender, never the primary-lender fallback; null until the
  // journey has said which lender it is (see journeyLenderCore.js).
  const identity = useJourneyLender(kfsData?.lender);
  const [linkDrawer, setLinkDrawer] = useState(false);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [isMaintenanceChecked, setIsMaintenanceChecked] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  // cpcore refused e-sign for good (a past disbursement date): Next stays disabled, because
  // tapping again cannot change the answer. The reason shows as the error message.
  const [blocked, setBlocked] = useState(false);
  // Gromor only: once Next has been tapped, the rest of eSign happens in a drawer over this
  // screen — waiting for the agreement, reading it, ticking. See GromorEsignFlow.
  const [gromorFlowOpen, setGromorFlowOpen] = useState(false);
  const isGromor = kfsData?.lender === "GROMOR";

  const iframeContainerRef = useRef(null);
  // start-digio can now keep retrying for ~30 s (startDigioWithRetry); if the tenant leaves
  // this screen meanwhile, the late answer must not navigate or redirect them from elsewhere.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const token = localStorage.getItem(config.LOCAL_KEYS.SIGNUP_TOKEN);
  const partner = localStorage.getItem("partnerIdentifier");

  const pdfUrl = `${config.API_URL}/file/${kfsData?.user?.userId}?path=agreements/sanction.pdf&token=${token}`;
  const kfsUrl = `${config.API_URL}/file/${kfsData?.user?.userId}?path=agreements/draft_application_sanction_kfs.pdf&token=${token}`;

  useEffect(() => {
    const getData = async () => {
      try {
        setLoading(true);
        const journeyData = await fetchData("journey/full");
        setKfsData(journeyData.journey);

        if (journeyData.journey?.lender === "GROMOR") {
          const status = await fetchGromorEsignStatus().catch(() => null);
          if (status?.complete) {
            navigate(
              `/b/${localStorage.getItem("user_identifier")}?partner=${partner}&step=mandate`
            );
            return;
          }
          // Not complete: hold on this screen. The agreement opens only on Continue, which is
          // idempotent server-side, so a returning tenant simply taps it again.
        }
      } catch (error) {
        console.error("Error fetching journey data", error);
      } finally {
        setLoading(false);
      }
    };
    getData();
  }, []);

  useEffect(() => {
    if (!loading) {
      setLoadingText(ESIGN_LOADING_STAGES[0].text);
      return;
    }
    // Stages are at different offsets (0s / 8s / 30s), not evenly spaced, so each gets
    // its own timeout rather than one repeating interval.
    const timers = ESIGN_LOADING_STAGES.slice(1).map((stage) =>
      setTimeout(() => setLoadingText(stage.text), stage.atMs)
    );
    return () => timers.forEach(clearTimeout);
  }, [loading]);

  function formatDate(dateString) {
    const suffix = (day) => {
      if (day % 10 === 1 && day !== 11) return "st";
      if (day % 10 === 2 && day !== 12) return "nd";
      if (day % 10 === 3 && day !== 13) return "rd";
      return "th";
    };

    const date = new Date(dateString);
    const day = date.getDate();
    const formattedDay = `${day}${suffix(day)}`;
    const formattedDate = format(date, `MMMM yyyy`);
    return `${formattedDay} ${formattedDate}`;
  }

  const sendApiCall = async () => {
    try {
      setLoading(true);

      const platform =
        navigator?.userAgentData?.platform ||
        navigator.platform ||
        localStorage.getItem("UTM_SOURCE");

      // Record consent first and wait for it to complete
      // This ensures the document gets the latest timestamp
      await trackConsent(
        "Onclick",
        "ESign Consent",
        "",
        platform,
        CONSENT_MESSAGES.ESIGN
      );

      if (isGromor) {
        // Clickwrap: no Digio session, no redirect. The backend sends the proposal (deposit
        // only if the toggle is on), asks Gromor for the agreement, and we wait for it here.
        // Idempotent server-side, so a double-tap cannot send the proposal twice.
        await startGromorEsign({ maintenanceOn: Boolean(isMaintenanceChecked) });
        setGromorFlowOpen(true);
        return;
      }

      // Generate the document after consent API completes
      const next = await startDigioWithRetry(() => fetchData("esign/start-digio"), { partner });
      if (!mountedRef.current) return;
      if (next.kind === "signed") {
        // Same route Digio's own success redirect lands on, so the existing
        // processing → mandate routing (ProcessDisbursal.jsx) handles B2B and D2C alike.
        navigate("/processing?source=esign&status=success");
        return;
      }
      if (next.kind === "blocked") {
        setBlocked(true);
        setErrorMessage(next.message);
        return;
      }
      if (next.kind === "error") {
        setErrorMessage(next.message || "Something went wrong on our end. Please try again in a few minutes.");
        return;
      }
      window.location.href = next.url;
    } catch (error) {
      console.log(error);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  const handleCompleteESign = () => {
    setLinkDrawer(true);
  };

  const handleIframeScroll = () => {
    const iframeContainer = iframeContainerRef.current;
    if (!iframeContainer) return;

    const { scrollTop, scrollHeight, clientHeight } = iframeContainer;

    if (scrollTop + clientHeight >= scrollHeight - 10) {
      setScrolledToBottom(true);
    } else {
      setScrolledToBottom(false);
    }
  };

  if (!kfsData) {
    return <div className="text-center text-white">Loading...</div>;
  }

  return (
    <Div100vh
      className={`flex flex-col justify-between gap-4 py-6 overflow-y-scroll ${
        theme?.backgroundColor && theme.backgroundColor
      }`}
    >
      <div>
        <div className="flex flex-col px-10">
          {theme?.headingColor ? (
            <HeaderB2B
              // heading="Sanction Details"
              heading="Complete ESign"
              // subheading="Move-In With Ease"
            />
          ) : (
            <Header
              heading="Sanction Details"
              subheading="Move-In With Ease"
              showChat={false}
            />
          )}
        </div>

        <div className="flex flex-col mx-4 gap-4 text-xs text-[#eeeeee] font-medium rounded-lg px-4 py-8">
          <div className="px-4 py-2.5 rounded-md border border-[#343434] justify-start items-center gap-2.5 inline-flex">
            <span className={`${theme?.headingColor} w-3/5`}>
              Funding Partner
            </span>
            <span className="flex w-2/5 justify-end text-right text-[#a4a4a4]">
              {identity?.legalName}
            </span>
          </div>

          <div className="flex px-4 py-2.5 rounded-md border border-[#343434] justify-start items-center gap-2.5">
            <div className="flex flex-col w-3/5">
              <span className={`${theme?.headingColor} `}>Monthly Rent</span>
              {!theme && (
                <span className="text-[#a4a4a4] text-[8px] font-medium">
                  No Cost EMI
                </span>
              )}
            </div>
            <span
              className={`flex w-2/5 justify-end ${
                theme?.headingColor
                  ? `${theme?.headingColor}`
                  : "text-[#1987fb]"
              } text-right`}
            >
              ₹ {kfsData?.emi_amount || kfsData?.rent}
            </span>
          </div>

          <div className="px-4 py-2.5 rounded-md border border-[#343434] justify-start items-center gap-2.5 inline-flex">
            <span className={`${theme?.headingColor} w-3/5`}>Rent Tenure</span>
            <span className="flex w-2/5 justify-end text-right text-[#a4a4a4]">
              {kfsData?.emi_tenure || kfsData?.loanTenure} months
            </span>
          </div>

          <div className="px-4 py-2.5 rounded-md border border-[#343434] justify-start items-center gap-2.5 inline-flex">
            <div className="flex flex-col w-3/5">
              <span className={`${theme?.headingColor}`}>
                First Rent Due Date
              </span>
              <span className="text-[#a4a4a4] text-[8px] font-medium">
                Auto Debit Every Month
              </span>
            </div>
            <span className="flex w-2/5 justify-end text-right text-[#a4a4a4]">
              {formatDate(kfsData?.firstEmiDate) || "NA"}
            </span>
          </div>

          {theme && kfsData?.maintenanceAmount && partner !== "kots" && (
            <div className="px-4 py-2.5 rounded-md border border-[#343434] flex justify-between items-center">
              <span className={`flex gap-1 ${theme?.headingColor}`}>
                Maintenance Charges{" "}
                <Tooltip
                  text={`The Maintenance Cover/Security Deposit is an amount CirclePe pays to ${
                    theme?.name || ""
                  } for potential accommodation damages. You'll only be charged this amount at the time of your move-out or opting out of CirclePe's services.`}
                  position="top"
                />
              </span>
              <div className="flex flex-col items-center gap-2">
                <span
                  className={`flex justify-end ${
                    theme?.headingColor
                      ? `${theme?.headingColor}`
                      : "text-[#1987fb]"
                  } text-right`}
                >
                  ₹ {kfsData?.maintenanceAmount || "N/A"}
                </span>

                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isMaintenanceChecked}
                    onChange={() =>
                      setIsMaintenanceChecked((prevState) => !prevState)
                    }
                    value=""
                    className="sr-only peer"
                  />
                  <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600 dark:peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
          )}
        </div>
      </div>

      <CustomDrawer isOpen={linkDrawer}>
        {/* <GoBackButton customBackSubmit={()=>setLinkDrawer(false)}/> */}
        <div className="h-full w-full absolute left-0 top-0">
          <iframe
            src={kfsUrl}
            title="KFS Letter"
            className="w-full h-full rounded-xl border border-[#444] my-4"
            allowFullScreen
          ></iframe>
        </div>
        <button
          onClick={() => setLinkDrawer(false)}
          className="absolute top-8 left-8 p-2 cursor-pointer hover:bg-slate-100 se:w-8 se:h-8 sm:h-11 sm:w-11 bg-white rounded-full flex flex-col justify-center items-center"
        >
          <ChevronLeftIcon className="size-112  text-black" />
        </button>
      </CustomDrawer>

      {isGromor && (
        <GromorEsignFlow
          journey={kfsData}
          lender={identity}
          isOpen={gromorFlowOpen}
          onClose={() => setGromorFlowOpen(false)}
        />
      )}

      {/* <CustomDrawer
        isOpen={linkDrawer}
        closeDrawer={() => {
          setLinkDrawer(false);
          setScrolledToBottom(false);
          setConsentChecked(false);
        }}
      >
        <div className="h-full w-full flex flex-col relative">
          <div
            ref={iframeContainerRef}
            className="flex-1 overflow-y-auto"
            onScroll={handleIframeScroll}
          >
            <iframe
              src={pdfUrl}
              title="Sanction Letter"
              className="w-full h-[calc(100vh-200px)]"
              style={{ border: "none" }}
              allowFullScreen
            />
          </div>

          <div className="bg-[#1a1a1a] p-4 text-white sticky bottom-0 left-0 w-full border-t border-[#444]">
            {true ? (
              <>
                <p className="text-3xs mb-2">
                  I,{" "}
                  <strong>
                    {kfsData?.user?.firstName} {kfsData?.user?.lastName}
                  </strong>
                  , hereby confirm that I have read, understood / been explained
                  in the language understood by me, the Loan Offer detailed in
                  these Sanction Documents & Key Fact Statement (KFS) along with
                  the Most Important Terms & Conditions (“MITC”), and I accord
                  my consent to the same.
                </p>

                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="consentCheckbox"
                    checked={consentChecked}
                    onChange={(e) => setConsentChecked(e.target.checked)}
                    className="cursor-pointer"
                  />
                  <label htmlFor="consentCheckbox" className="cursor-pointer">
                    I Agree
                  </label>
                </div>

                <PrimaryButton
                  text="Proceed"
                  loading={loading}
                  handleClick={sendApiCall}
                  disabled={!consentChecked}
                />
              </>
            ) : (
              <p className="text-sm text-gray-400">
                Scroll to the bottom to proceed.
              </p>
            )}
          </div>
        </div>
      </CustomDrawer> */}
      <div className="flex flex-col px-6">
        <div className={`text-3xs mb-2 ${theme?.textColor}`}>
          I,{" "}
          <strong>
            {kfsData?.user?.firstName} {kfsData?.user?.lastName}
          </strong>
          , hereby confirm that I have read, understood/been explained in the
          language understood by me, the Loan Offer detailed in these{" "}
          <button
            className="inline-flex items-start text-3xs text-blue-700 underline"
            onClick={() => setLinkDrawer(true)}
          >
            Sanction Documents & Key Fact Statement (KFS)
          </button>{" "}
          along with the Most Important Terms & Conditions (“MITC”), and I
          accord my consent to the same.
        </div>

        {errorMessage && (
          <p className="text-red-500 text-sm text-center mb-2">{errorMessage}</p>
        )}
        <div className="flex flex-col items-center justify-center">
          <PrimaryButton
            // text="View Sanction"
            // handleClick={handleCompleteESign}
            text="Next"
            handleClick={sendApiCall}
            disabled={
              loading ||
              blocked ||
              (!isGromor && kfsData?.maintenanceAmount && !isMaintenanceChecked)
            }
            loading={loading}
            loadingText={loadingText}
          />
          {identity && (
            <div className="flex items-center justify-center text-black gap-1">
              <span className="text-black text-2xs italic">powered by</span>
              <a href={identity.homeUrl}>
                <img
                  src={identity.logoUrl}
                  alt={identity.legalName}
                  className="inline-block max-h-4 ml-1"
                />
              </a>
            </div>
          )}
        </div>
      </div>
    </Div100vh>
  );
}
